/** Contract evaluator. No native model IDs or model-specific limits belong here. */
import { MODEL_CAPABILITIES, VIDEO_MODEL_CANONICAL_ID } from '../../../../generated/modelCapabilities'
import type { MediaInput, ModelInputContract, ParameterRule } from '../../../../generated/modelInputContractTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { getVideoReferencePolicy, getByomConstraints } from './videoConstraints'

export type CapabilitiesMap = Record<string, string[] | string>
export type { BlockReason, GenerationRequest } from './inputContractEvaluation'
export { evaluateContract, parameterRuleAllows, resolveParameterRule } from './inputContractEvaluation'
import { normalizeRequest, evaluateContract, type BlockReason, type GenerationRequest } from './inputContractEvaluation'

export function hasCapabilities(modelId: string): boolean {
  return resolveInputContract(modelId) !== undefined
}

function byomInputContract(modelId: string, capabilities: CapabilitiesMap | null | undefined): ModelInputContract {
  const policy = getVideoReferencePolicy(modelId, capabilities)
  const media = (port: string, kind: MediaInput['media'], max: number | null): MediaInput => ({
    port, media: kind, max_count: max, min_seconds_each: null,
    max_seconds_each: null, max_seconds_combined: null, metadata_rules: [],
  })
  const parameters: ParameterRule[] = []
  const options = (parameter: string, raw: string[] | string | undefined, reasonCode: string) => {
    if (!Array.isArray(raw)) return
    const allowed = [...new Set(raw.map((value) => value.trim()).filter(Boolean))].sort()
    if (allowed.length === 0) return
    parameters.push({ parameter, kind: 'enum', disposition: 'validate', allowed,
      default: null, default_sentinels: [], case_insensitive: false, minimum: null, maximum: null, step: null, unit: null, when: [], reason_code: reasonCode })
  }
  options('aspect_ratio', capabilities?.aspect_ratios, 'aspectRatioInvalid')
  options('resolution', capabilities?.resolutions, 'resolutionInvalid')
  options('quality', capabilities?.quality, 'parameterInvalid')
  const duration = getByomConstraints(capabilities ?? null).durationOptions
  if (duration) parameters.push({ parameter: 'duration', kind: 'number', disposition: 'validate',
    allowed: duration, default: duration[duration.length - 1], default_sentinels: [], case_insensitive: false, minimum: duration[0],
    maximum: duration[duration.length - 1], step: 1, unit: 'seconds', when: [], reason_code: 'durationInvalid' })
  return {
    schema_version: 1,
    inputs: [media('in_images', 'image', policy.maxImages),
      media('in_reference_videos', 'video', policy.maxVideos),
      media('in_reference_audios', 'audio', policy.maxAudios)],
    max_total_references: policy.maxTotalReferences,
    requirements: [], parameters,
  }
}

export function resolveInputContract(modelId: string, capabilities?: CapabilitiesMap | null): ModelInputContract | undefined {
  if (modelId.startsWith('gateway/')) return byomInputContract(modelId, capabilities)
  return (MODEL_CAPABILITIES[modelId] ?? MODEL_CAPABILITIES[VIDEO_MODEL_CANONICAL_ID[modelId]])?.inputContract
}


const contractLog = createNadDebug('dw:model-input-contract', { priority: true })

export function preflight(request: GenerationRequest, modelLabel?: string, boundary = 'console-preflight'): BlockReason[] {
  const contract = resolveInputContract(request.modelId, request.byomCapabilities)
  const normalized = contract ? normalizeRequest(contract, request) : request
  const reasons = contract ? evaluateContract(contract, normalized, modelLabel ?? request.modelId) : [{ code: 'modelUnavailable', params: { model: modelLabel ?? request.modelId } }]
  const declared = MODEL_CAPABILITIES[request.modelId] ?? MODEL_CAPABILITIES[VIDEO_MODEL_CANONICAL_ID[request.modelId]]
  contractLog({ event: reasons.length ? 'model-input-contract:blocked' : 'model-input-contract:allowed',
    validation_boundary: boundary, model_id: request.modelId,
    contract_fingerprint: declared?.inputContractFingerprint ?? null,
    schema_version: contract?.schema_version ?? null,
    phase: request.phase ?? 'execute', input_counts: { ...request.inputCounts }, reference_order: request.referenceOrder,
    parameters: { ...normalized.parameters }, codes: JSON.stringify(reasons.map((reason) => reason.code)),
    decision: reasons.length ? 'blocked' : 'allowed' })
  return reasons
}

export function getOfferedAspectRatios(model: string): readonly string[] | null {
  return (MODEL_CAPABILITIES[model] ?? MODEL_CAPABILITIES[VIDEO_MODEL_CANONICAL_ID[model]])?.offeredAspectRatios ?? null
}
export function getOfferedAspectRatioDefault(model: string): string | null {
  const declared = MODEL_CAPABILITIES[model] ?? MODEL_CAPABILITIES[VIDEO_MODEL_CANONICAL_ID[model]]
  return declared?.offeredAspectRatioDefault ?? declared?.offeredAspectRatios[0] ?? null
}
